import { ContentItemType } from '@/client/courses';
import { QuizAnswerStatistics } from '@/client/quizzes';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { ResultCard } from '@/components/courses/ResultCard';
import { useCourse } from '@/hooks/query';
import { useQuizzesAnswersStatistics } from '@/hooks/query/quizzes.hooks';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import { AppButton } from '@/ui/buttons';
import { CardGridContainer, FlexContainer } from '@/ui/styled-ui';
import { ResultsIcon } from '@/utils/helpers';
import { Divider } from 'primereact/divider';
import { ProgressSpinner } from 'primereact/progressspinner';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { CoursesActionHeader } from './CoursesActionHeader';
import { CoursesTabs } from './CoursesTabs';

const StyledResults = styled(FlexContainer)`
  background-color: var(--beige-main);
  border-radius: var(--medium-border-radius);
  padding: var(--default-padding);
  justify-content: flex-start;
  align-items: flex-start;
  flex: 1;

  h2 {
    margin-top: 0;
  }

  h3 {
    font-size: var(--medium-font-size);
    font-weight: 400;
    margin: 0;
  }

  .p-divider {
    margin: var(--semi-big-padding) 0;
  }
`;

const StyledNoResults = styled(FlexContainer)`
  align-items: center;
  justify-content: center;
  height: 100%;
  flex: 1;
  text-align: center;
  h2 {
    font-size: var(--heading-medium-font-size);
    margin-bottom: var(--default-padding);
  }
  p {
    font-size: var(--medium-font-size);
    line-height: var(--medium-line-height);
    max-width: 520px;
    margin: 0;
    margin-bottom: var(--semi-big-padding);
    text-align: center;
  }
`;

const StyledCardGridContainer = styled(CardGridContainer)`
  width: 100%;
  grid-template-columns: repeat(2, 1fr);
  margin: 0;
`;

export const CoursesResultsPage: React.FC = () => {
  const { t } = useTranslation();
  const { quizId, courseId } = useParams();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const navigate = useNavigate();

  const { course, isLoading: isCourseLoading } = useCourse({
    courseId,
    staleTime: Infinity,
  });

  const { quizzesAnswers, isLoading } = useQuizzesAnswersStatistics(
    currentAccount?.id as string,
    quizId as string,
  );

  return (
    <FlexContainer
      id="courses-results-page"
      direction="column"
      align="flex-start"
      justify="stretch"
      minHeight="100%"
    >
      {(isLoading || isCourseLoading) && (
        <FlexContainer direction="column" className="mt-5">
          <ProgressSpinner />
          <h3>{t('results.loading')}</h3>
        </FlexContainer>
      )}
      {!isLoading && !isCourseLoading && course && (
        <>
          <CoursesActionHeader course={course} />
          {course && <CoursesTabs course={course} />}
          <StyledResults direction="column">
            {quizzesAnswers && !!quizzesAnswers?.length ? (
              <>
                <FlexContainer gap={16} justify="flex-start" align="stretch">
                  <ResultsIcon />
                  <FlexContainer
                    direction="column"
                    gap={4}
                    align="space-between"
                  >
                    <h3>{t('results.header.p1')}</h3>
                    <h3>{t('results.header.p2')}</h3>
                  </FlexContainer>
                </FlexContainer>
                <Divider color="var(--gray-moderate)" />
                <h2>
                  {
                    course.courseContents?.find(
                      (content) => content.type === ContentItemType.QUIZ,
                    )?.quiz?.title
                  }
                </h2>
                <StyledCardGridContainer>
                  {quizzesAnswers.map(
                    (item: QuizAnswerStatistics, index: number) => (
                      <ResultCard
                        key={item.questionId}
                        questionNumber={index + 1}
                        questionText={item.questionLabel}
                        firstAttemptPercentage={Number(
                          item.correctFirstAttempts,
                        )}
                        subsequentAttemptsPercentage={Number(
                          item.correctSubsequentAttempts,
                        )}
                        totalEnrolledUsers={Number(item.totalEnrolledUsers)}
                        totalSubsequentUsers={Number(item.totalSubsequentUsers)}
                      />
                    ),
                  )}
                </StyledCardGridContainer>
              </>
            ) : (
              <StyledNoResults direction="column">
                <h2>{t('results.noResults')}</h2>
                <p>{t('results.noResults.p')}</p>
                <AppButton
                  label={t('results.noResults.cta')}
                  severity="success"
                  onClick={() =>
                    navigate(
                      RedirectPaths[RedirectPathsEnum.ACCOUNT_COURSE_PLANNER](),
                    )
                  }
                />
              </StyledNoResults>
            )}
          </StyledResults>
        </>
      )}
    </FlexContainer>
  );
};
